import classNames from 'classnames';
import { Trash2 } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ApiDataFieldKey } from './ApiDataTable';
import { ApiDataItemDto } from '@/Domain/api/Action/dto/Action.dto';
import { InlineEditor } from '@/Domain/components/Action/Lexical/InlineEditor';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { FormMessage as CustomFormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type Props = {
  index: number;
  name: string;
  arrayField: any;
  dataFieldKey: ApiDataFieldKey;
  withInsertBadge: boolean;
  onRemove: (index: number) => void;
};

export const ApiDataItem = ({ index, name, onRemove, withInsertBadge, dataFieldKey }: Props) => {
  const { t } = useTranslation('actions_page');

  const parameters = useWatch({ name: 'parameters' });
  const values = useWatch({ name });
  const allKeys = values.map((item: ApiDataItemDto) => item.name);

  return (
    <>
      <section
        className={classNames(
          'grid gap-4 grid-cols-[_minmax(0,540px)_minmax(0,540px)_auto_auto] xl:grid-cols-[_minmax(0,700px)_minmax(0,700px)_auto_auto] 2xl:grid-cols-[_minmax(0,500px)_minmax(0,500px)_auto_auto] items-start p-4',
        )}
      >
        <FormField
          name={`${name}.${index}.name`}
          render={({ field, fieldState }) => {
            const value = field.value || '';
            const isKeyUnique = allKeys.filter((name: string) => name === value).length > 1;

            const errorMessage = isKeyUnique
              ? {
                  type: z.ZodIssueCode.custom,
                  message: t('sheet.form.validation.key_unique'),
                }
              : undefined;

            return (
              <FormItem>
                <FormControl>
                  <div data-testid="api-data-item-key">
                    <InlineEditor
                      size="sm"
                      field={field}
                      error={fieldState.error || errorMessage}
                      parameters={parameters}
                      withInsertBadge={withInsertBadge}
                      placeholder={t('sheet.form.api_endpoint.api_data.table.key')}
                    />
                  </div>
                </FormControl>
                <CustomFormMessage error={fieldState.error || errorMessage} />
              </FormItem>
            );
          }}
        />

        <div data-testid="api-data-item-value">
          <FormField
            name={`${name}.${index}.${dataFieldKey}`}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <InlineEditor
                    size="sm"
                    field={field}
                    parameters={parameters}
                    error={fieldState.error}
                    withInsertBadge={withInsertBadge}
                    placeholder={t('sheet.form.api_endpoint.api_data.table.value')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Separator orientation="vertical" className="h-[34px]" />
        <section className="flex gap-2 justify-self-end">
          <Tooltip>
            <TooltipTrigger tabIndex={-1} asChild>
              <Button
                variant="secondary"
                size="icon"
                className="hover:!text-red-500"
                onClick={() => onRemove(index)}
              >
                <Trash2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('tooltip.delete')}</TooltipContent>
          </Tooltip>
        </section>
      </section>
    </>
  );
};
